import { service } from '@ember/service';
import { Ability } from 'ember-can';
import type AbilitiesService from 'ember-can/services/abilities';
import config from 'garaje/config/environment';
import type AuthzService from 'garaje/services/authz';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type StateService from 'garaje/services/state';
import { Permission } from 'garaje/utils/ui-permissions';

export default class ManageSettingsAbility extends Ability {
  @service declare abilities: AbilitiesService;
  @service declare authz: AuthzService;
  @service declare featureFlags: FeatureFlagsService;
  @service declare state: StateService;

  get canVisitLocal(): boolean {
    return (
      this.canVisitLocalAdmins ||
      this.canVisitLocalCustomRoles ||
      this.canUpdateLocalSettings ||
      this.canVisitLocalEventLog
    );
  }

  get canVisitLocalAdmins(): boolean {
    return this.authz.hasPermissionAtCurrentLocation(Permission.USER_MANAGEMENT_ADMIN_READ);
  }

  get canVisitLocalCustomRoles(): boolean {
    return this.authz.hasPermissionAtCurrentLocation(Permission.USER_MANAGEMENT_CUSTOM_ROLE_READ);
  }

  get canUpdateLocalSettings(): boolean {
    const permissions = [
      Permission.ACCOUNT_MANAGEMENT_LOCATION_UPDATE,
      Permission.VISITORS_LOCATION_CONFIGURATION_UPDATE_ADDRESS,
      Permission.VISITORS_LOCATION_CONFIGURATION_UPDATE_BRANDING,
      Permission.VISITORS_LOCATION_CONFIGURATION_GENERATE_KEY,
      Permission.VISITORS_LOCATION_CONFIGURATION_UPDATE_LANGUAGE,
      Permission.VISITORS_LOCATION_CONFIGURATION_UPDATE_DISABLED,
      Permission.VISITORS_LOCATION_CONFIGURATION_UPDATE_CAPACITY,
      Permission.VISITORS_LOCATION_CONFIGURATION_UPDATE_WORKPLACE_UTILIZATION,
      Permission.VISITORS_LOCATION_CONFIGURATION_UPDATE_SIGN_OUT,
      Permission.VISITORS_LOCATION_CONFIGURATION_MANAGE_TENANCY,
      Permission.VISITORS_GDPR_CONFIGURATION_CREATE,
      Permission.VISITORS_GDPR_CONFIGURATION_UPDATE,
      Permission.VISITORS_GDPR_CONFIGURATION_DELETE,
    ];

    const hasAnyLocationPerm =
      this.authz.hasAnyPermissionAtCurrentLocation(permissions) ||
      this.authz.hasAnyPermissionAtCurrentCompany(permissions);

    // Don't show Manage if user ONLY has location.update (for flow assignment or scheduling settings)
    // without any other location management permissions
    const employeeFlowEnabled = this.featureFlags.isEnabled('workplace-permission-employee-registration-flow');
    const schedulingEnabled = this.featureFlags.isEnabled('workplace-permission-employee-scheduling-settings');

    if (employeeFlowEnabled || schedulingEnabled) {
      const hasLocationUpdate =
        this.authz.hasPermissionAtCurrentCompany(Permission.ACCOUNT_MANAGEMENT_LOCATION_UPDATE) ||
        this.authz.hasPermissionAtCurrentLocation(Permission.ACCOUNT_MANAGEMENT_LOCATION_UPDATE);

      const otherPerms = permissions.filter((p) => p !== Permission.ACCOUNT_MANAGEMENT_LOCATION_UPDATE);
      const hasOtherLocationPerms =
        this.authz.hasAnyPermissionAtCurrentCompany(otherPerms) ||
        this.authz.hasAnyPermissionAtCurrentLocation(otherPerms);

      if (hasLocationUpdate && !hasOtherLocationPerms) {
        return false;
      }
    }

    return hasAnyLocationPerm;
  }

  get canVisitLocalEventLog(): boolean {
    return Boolean(
      !this.#isEURegion() &&
        this.state.features?.canAccessEventLog &&
        this.featureFlags.isEnabled('audit-log') &&
        this.authz.hasPermissionAtCurrentLocation(Permission.USER_MANAGEMENT_ACTIVITY_LOG_READ),
    );
  }

  get canVisitGlobal(): boolean {
    return (
      this.canVisitAccountSettings ||
      this.canVisitGlobalAdmins ||
      this.canVisitGlobalCustomRoles ||
      this.canVisitGlobalEventLog
    );
  }

  get canVisitAccountSettings(): boolean {
    const canAccessAccountCompanySettings = this.authz.hasPermissionAtCurrentCompany(
      Permission.ACCOUNT_MANAGEMENT_COMPANY_CONFIG_UPDATE,
    );
    const canAccessAccountProductSettings = this.authz.hasPermissionAtCurrentCompany(
      Permission.ACCOUNT_MANAGEMENT_PRODUCT_SETTINGS_READ,
    );

    return (this.canAccessInactivityLogout && canAccessAccountCompanySettings) || canAccessAccountProductSettings;
  }

  get canVisitVisitorSettings(): boolean {
    return this.authz.hasPermissionAtCurrentCompany(Permission.ACCOUNT_MANAGEMENT_COMPANY_CONFIG_READ);
  }

  get canManageVisitorSettings(): boolean {
    return this.authz.hasPermissionAtCurrentCompany(Permission.ACCOUNT_MANAGEMENT_COMPANY_CONFIG_UPDATE);
  }

  get canVisitGlobalAdmins(): boolean {
    return this.authz.hasPermissionAtCurrentCompany(Permission.USER_MANAGEMENT_ADMIN_READ);
  }

  get canVisitGlobalCustomRoles(): boolean {
    return this.authz.hasPermissionAtCurrentCompany(Permission.USER_MANAGEMENT_CUSTOM_ROLE_READ);
  }

  get canVisitGlobalEventLog(): boolean {
    return Boolean(
      !this.#isEURegion() &&
        this.state.features?.canAccessEventLog &&
        this.featureFlags.isEnabled('audit-log') &&
        this.authz.hasPermissionAtCurrentCompany(Permission.USER_MANAGEMENT_ACTIVITY_LOG_READ),
    );
  }

  get canAccessInactivityLogout(): boolean {
    return !!this.state.features?.canAccessInactivityLogout;
  }

  #isEURegion(): boolean {
    return config.environment === 'eu';
  }
}
